import { useImageByBreakpointParams } from "@/types";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useImageByBreakpoint
 *
 * @param {string} breakpoint - vuetify breakpoint
 * @param {string} folder - base directory for images
 * @param {object} images - object of image file names keyed by vuetify breakpoint
 * @param {boolean} mounted - flag to know if we're mounted or not
 * @returns {null|string} - null or path to the file
 *
 * @example Basic Usage
 * ```ts
 * const vuetify = useVuetify();
 * const breakpoint = computed(() => vuetify.breakpoint.name);
 * const mounted = ref(false);
 *
 * const breakpointImage = (dpr) => useImageByBreakpoint({
 *   breakpoint,
 *   folder: "images/page/project",
 *   images: {
 *     "xl lg md": `md/${dpr}x.jpg`,
 *     "sm xs": `sm/${dpr}x.jpg`
 *   },
 *   mounted
 * });
 *
 * const image = computed(() => breakpointImage("1"));
 * const imageSrcSet = props.drps.map((dpr) => `${breakpointImage(dpr)} ${dpr}x`).join(", ");
 * ```
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useImageByBreakpoint = ({ breakpoint, folder, images, mounted }: useImageByBreakpointParams) => {
  if (
    mounted?.value === undefined ||
    mounted?.value === null ||
    breakpoint?.value === undefined ||
    breakpoint?.value === null ||
    !folder ||
    !images ||
    Object.keys(images).length === 0
  ) {
    throw new Error("Missing required attributes for useImageByBreakpoint");
  }

  if (mounted?.value === false) {
    return null;
  }

  const imageKey = Object.keys(images).find((key) => key.includes(breakpoint.value));

  if (imageKey) {
    return require(`@/assets/images/${folder}/${images[imageKey]}`);
  } else {
    return null;
  }
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useImageSrcset
 * @param    {string} assetPath  Folder where the the image file is in the assets folder
 * @param    {string} imageName  Name of the image file
 * @param    {number[]} sizes    Array of numbers that represent the image sizes
 * @desc     A composable that build a srcset string for responsive images based
 *          on the provided parameters
 *
 * @summary  Composable returns a source set for responsive images
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useImageSrcset = (assetPath: string, imageName: string, sizes: number[]) => {
  return sizes.map((s) => require(`@/assets/${assetPath}/${imageName}-${s}x.jpg`) + ` ${s}x`).join(", ");
};
